import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  DataTable,
  EmptyState,
  BlockStack,
  Text,
  TextField,
  Spinner,
  InlineStack,
  Badge,
  Button,
  Banner,
} from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { reviewsApi } from '../utils/api';
import { isLocalDevelopment } from '../utils/env';
import { useNavigate } from 'react-router-dom';

interface ProductWithReviews {
  id: string;
  title: string;
  handle: string;
  totalReviews: number;
  averageRating: number;
  pendingReviews: number;
}

const Products: React.FC = () => {
  const { t } = useTranslation();
  const { shopId, shopDomain } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<ProductWithReviews[]>([]);
  const [search, setSearch] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isPaid, setIsPaid] = useState(false);
  const [productsWithReviewsCount, setProductsWithReviewsCount] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!shopId) {
      setLoading(false);
      return;
    }

    fetchProducts();
  }, [shopId]);

  const fetchProducts = async () => {
    if (!shopId) {
      setLoading(false);
      return;
    }

    // Em desenvolvimento local com shopId de mock, não fazer requisição
    if (isLocalDevelopment() && shopId === 'local-dev-shop-id') {
      setLoading(false);
      setProducts([]);
      return;
    }

    setLoading(true);

    try {
      // Buscar produtos via Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5pbGhvc3Fndm1zYWJubGpwYXZvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjQ4MTczNzksImV4cCI6MjA4MDM5MzM3OX0.OCjijhy9q77bsPZxy81n2btASqUy50k2hjG9ROXLxjU';

      if (!supabaseAnonKey) {
        const errorMsg = 'Supabase configuration not found. Please check environment variables.';
        setError(errorMsg);
        setLoading(false);
        return;
      }

      const productsUrl = `${supabaseUrl}/functions/v1/products?shopId=${encodeURIComponent(shopId)}&limit=50`;

      // Log para diagnóstico (remover em produção)
      console.log('Fetching products for shopId:', shopId);

      const response = await fetch(productsUrl, {
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || `HTTP ${response.status}` };
        }
        
        // Log detalhado do erro para diagnóstico
        console.error('Error fetching products:', {
          status: response.status,
          statusText: response.statusText,
          shopId,
          errorData
        });
        
        // Tratamento especial para erro 401 (token inválido)
        if (response.status === 401) {
          setError(
            `🔐 Token de acesso inválido ou expirado. ` +
            `Por favor, reinstale o app clicando no botão abaixo.`
          );
          setLoading(false);
          return;
        }
        
        // Mensagem de erro mais detalhada para outros erros
        let errorMsg = errorData.error || `Error fetching products (${response.status})`;
        if (errorData.details) {
          if (errorData.details.message) {
            errorMsg = errorData.details.message;
          } else if (errorData.details.errors) {
            errorMsg += `: ${JSON.stringify(errorData.details.errors)}`;
          } else if (typeof errorData.details === 'string') {
            errorMsg += `: ${errorData.details}`;
          }
        }
        
        setError(errorMsg);
        setLoading(false);
        return;
      }

      const result = await response.json();
      const shopifyProducts = result.products || [];

      // Verificar informações do plano
      if (result.plan) {
        setIsPaid(result.plan.isPaid || false);
        setProductsWithReviewsCount(result.plan.productsWithReviewsCount || 0);
        setLimitReached(result.plan.limitReached || false);
      }

      // Buscar todas as avaliações do shop (apenas se não for dev local)
      let allReviews: any[] = [];
      if (!isLocalDevelopment() || shopId !== 'local-dev-shop-id') {
        const reviewsResult = await reviewsApi.getAll(shopId);
        if (!reviewsResult.error) {
          allReviews = reviewsResult.data || [];
        }
      }

      // Combinar produtos com avaliações
      const productsWithReviews: ProductWithReviews[] = shopifyProducts.map((product: any) => {
        // O Supabase retorna product_id (snake_case)
        const productReviews = allReviews.filter(
          (review: any) => (review.product_id || review.productId) === product.id.toString()
        );
        
        const approvedReviews = productReviews.filter((r) => r.status === 'approved');
        const pendingReviews = productReviews.filter((r) => r.status === 'pending');
        
        const totalReviews = approvedReviews.length;
        const averageRating =
          totalReviews > 0
            ? approvedReviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews
            : 0;

        return {
          id: product.id.toString(),
          title: product.title,
          handle: product.handle,
          totalReviews,
          averageRating: Math.round(averageRating * 10) / 10,
          pendingReviews: pendingReviews.length,
        };
      });

      setProducts(productsWithReviews);
      setError(null);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error fetching products';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar produtos por busca
  const filteredProducts = products.filter((product) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return product.title.toLowerCase().includes(searchLower);
  });

  const rows = filteredProducts.map((product) => [
    product.title,
    product.totalReviews > 0 ? (
      <InlineStack gap="200" blockAlign="center">
        <Text as="span" variant="bodyMd">
          {'⭐'.repeat(Math.round(product.averageRating))}
        </Text>
        <Text as="span" variant="bodySm" tone="subdued">
          {product.averageRating.toFixed(1)}
        </Text>
      </InlineStack>
    ) : (
      <Text as="span" tone="subdued">Sem avaliações</Text>
    ),
    <InlineStack gap="200" blockAlign="center">
      <Text as="span" variant="bodyMd">
        {product.totalReviews}
      </Text>
      {product.pendingReviews > 0 && (
        <Badge tone="attention">
          {`${product.pendingReviews} pendente${product.pendingReviews > 1 ? 's' : ''}`}
        </Badge>
      )}
    </InlineStack>,
    <Button
      size="micro"
      onClick={() => window.location.href = `/reviews?productId=${product.id}`}
    >
      Ver Avaliações
    </Button>,
  ]);

  return (
    <Page
      title={t('products.title', { defaultValue: 'Produtos' })}
      primaryAction={{
        content: t('common.refresh', { defaultValue: 'Atualizar' }),
        onAction: fetchProducts,
      }}
    >
      {error && (
        <Banner 
          tone="critical" 
          onDismiss={() => setError(null)}
          action={
            error.includes('Token de acesso inválido') ? {
              content: 'Reinstalar App',
              onAction: () => {
                const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
                const shop = shopDomain || (typeof window !== 'undefined' ? window.location.hostname : 'ishop-management.myshopify.com');
                const authUrl = `${supabaseUrl}/functions/v1/auth?shop=${encodeURIComponent(shop)}`;
                window.location.href = authUrl;
              }
            } : undefined
          }
        >
          {error}
        </Banner>
      )}

      {/* Banner de limite atingido no FREE */}
      {limitReached && !isPaid && (
        <Banner
          tone="warning"
          title="Limite de produtos atingido"
          action={{
            content: 'Upgrade para PRO',
            onAction: () => navigate('/billing'),
          }}
        >
          Você atingiu o limite de 30 produtos no plano FREE. Faça upgrade para PRO para produtos ilimitados.
        </Banner>
      )}

      {/* Banner informativo sobre limite no FREE */}
      {!isPaid && !limitReached && productsWithReviewsCount > 0 && (
        <Banner
          tone="info"
          action={{
            content: 'Upgrade para PRO',
            onAction: () => navigate('/billing'),
          }}
        >
          Você está usando {productsWithReviewsCount} de 30 produtos disponíveis no plano FREE. Faça upgrade para PRO para produtos ilimitados.
        </Banner>
      )}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <TextField
              label={t('common.search')}
              value={search}
              onChange={setSearch}
              placeholder={t('products.searchPlaceholder', { defaultValue: 'Buscar produtos...' })}
              autoComplete="off"
            />
            {!isPaid && (
              <Badge tone={limitReached ? 'critical' : 'info'}>
                {`${productsWithReviewsCount} de 30 produtos (FREE)`}
              </Badge>
            )}
            {isPaid && (
              <Badge tone="success">
                Ilimitado (PRO)
              </Badge>
            )}
          </InlineStack>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <BlockStack gap="300" align="center">
                <Spinner size="large" />
                <Text as="p" tone="subdued" alignment="center">
                  {t('common.loading', { defaultValue: 'Loading...' })}
                </Text>
              </BlockStack>
            </div>
          ) : filteredProducts.length === 0 ? (
            <EmptyState
              heading={t('products.noProducts')}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>{t('products.noProductsDescription')}</p>
            </EmptyState>
          ) : (
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text']}
              headings={[
                t('products.fields.name', { defaultValue: 'Produto' }),
                t('products.fields.rating', { defaultValue: 'Avaliação Média' }),
                t('products.fields.reviews', { defaultValue: 'Total de Avaliações' }),
                t('products.fields.actions', { defaultValue: 'Ações' }),
              ]}
              rows={rows}
            />
          )}
        </BlockStack>
      </Card>
    </Page>
  );
};

export default Products;

